import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import {
  GridCellParams,
  GridColumnHeaderParams,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {t} from 'i18next';
import {isEqual, sortBy} from 'lodash';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import AurButton from '../../common/aur-button/AurButton';
import AurCheckbox from '../../common/aur-checkbox/AurCheckbox';
import AurDataGridPro from '../../common/aur-data-grid-pro/AurDataGridPro';

export interface RelationalGroup {
  id: number;
  key: string;
  name: string;
  disabled?: boolean;
}

interface Props extends Partial<DialogProps> {
  selectedIdArray: number[];
  relationalGroupArray?: RelationalGroup[];
  onSelectIds: (selectedIds: number[]) => void;
  onCancel?: () => void;
}

export interface RelationshipGroupPopupRef {
  openPopup: () => void;
}

const RelationshipGroupPopup = forwardRef(
  (props: Props, ref: Ref<RelationshipGroupPopupRef>) => {
    useImperativeHandle(ref, () => ({openPopup}));

    const {
      selectedIdArray,
      relationalGroupArray = [],
      onSelectIds,
      onCancel,
      ...rest
    } = props;

    const [isRelationshipPopupOpen, setIsRelationshipPopupOpen] =
      useState<boolean>(false);

    const [currentSelectedIdArray, setCurrentSelectedIdArray] =
      useState<number[]>(selectedIdArray);

    const isAllSelected: boolean = useMemo(
      () => currentSelectedIdArray.length === relationalGroupArray.length,
      [currentSelectedIdArray.length, relationalGroupArray.length]
    );

    const handleHeaderCheckboxClick = useCallback(() => {
      if (isAllSelected) {
        setCurrentSelectedIdArray([]);
      } else {
        setCurrentSelectedIdArray(relationalGroupArray.map((row) => row.id));
      }
    }, [isAllSelected, relationalGroupArray]);

    const renderIndexHeader = () => {
      return (
        <Box className={'aur-data-checkbox-cell'}>
          <Typography
            visibility={'hidden'}
            className={'typo-text body3_size aur-data-checkbox-index'}
          >
            {(relationalGroupArray ?? []).length}
          </Typography>
        </Box>
      );
    };

    const renderIndex = (params: GridRenderCellParams) => {
      const {value} = params;

      return (
        <Box className={'aur-data-checkbox-cell'}>
          <Typography
            className={'typo-text body3_size aur-data-checkbox-index'}
          >
            {value}
          </Typography>
        </Box>
      );
    };

    const renderValueHeaderCheckbox = (_: GridColumnHeaderParams) => {
      const isIndeterminate: boolean =
        currentSelectedIdArray.length > 0 && !isAllSelected;

      return (
        <Box className={'aur-data-checkbox-value-cell'}>
          <FormControlLabel
          control={ 
          <AurCheckbox
            inputProps={{
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              'data-testid': 'header-checkbox',
            }}
            className="aur-data-checkbox-id"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={handleHeaderCheckboxClick}
          />
          }
          label={<span className="sr-only">{t('aurCheckbox.checkedChecbox')}</span>}
          />
          <Typography
            className={'typo-text label1_size aur-data-checkbox-value'}
          >
            {t('indexSearchPage.relationshipGroupPopup.friendlyName')}
          </Typography>
        </Box>
      );
    };

    const renderValueCheckbox = (params: GridRenderCellParams) => {
      const {
        value,
        row: {id, name},
      } = params;

      const isChecked: boolean = currentSelectedIdArray.includes(id);

      return (
        <Box className={'aur-data-checkbox-value-cell'}>
          <FormControlLabel
          control={ 
          <AurCheckbox
            inputProps={{
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              'data-testid': `relationship-data-${value}`,
            }}
            className={'aur-data-checkbox-id'}
            checked={isChecked}
            onChange={() => handleCellClick(params)}
          />
          }
          label={<span className="sr-only">{t('aurCheckbox.checkedChecbox')}</span>}
          />
          <Typography
            className={'typo-text body3_size aur-data-checkbox-value'}
          >
            {name}
          </Typography>
        </Box>
      );
    };

    const columnArray = [
      {
        field: 'index',
        sortable: false,
        resizable: false,
        renderCell: renderIndex,
        renderHeader: renderIndexHeader,
        headerClassName: 'aur-data-checkbox-cell',
      },
      {
        field: 'name',
        renderCell: renderValueCheckbox,
        renderHeader: renderValueHeaderCheckbox,
        headerClassName: 'aur-data-checkbox-value-cell',
        flex: 1,
        sortable: false,
      },
    ];

    useEffect(() => {
      setCurrentSelectedIdArray(selectedIdArray);
    }, [selectedIdArray]);

    const openPopup = useCallback(() => {
      setIsRelationshipPopupOpen(true);
    }, []);

    const onCancelPopup = useCallback(() => {
      onCancel?.();
      setCurrentSelectedIdArray(selectedIdArray);
      setIsRelationshipPopupOpen(false);
    }, [selectedIdArray]);

    const onSaveData = useCallback(() => {
      onSelectIds(currentSelectedIdArray);
      setIsRelationshipPopupOpen(false);
    }, [currentSelectedIdArray, onSelectIds]);

    const handleCellClick = useCallback(
      (params: GridCellParams) => {
        const {id} = params;
        let selectedIds: number[] = [...currentSelectedIdArray];
        if (selectedIds.includes(id as number)) {
          selectedIds = selectedIds.filter((item) => item !== id);
        } else {
          selectedIds = [...selectedIds, id as number];
        }
        setCurrentSelectedIdArray([...selectedIds]);
      },
      [currentSelectedIdArray]
    );

    const renderPopupTitle = () => (
      <DialogTitle>
        <Typography className="typo-text headline3_size heading-text title-text">
          {t('indexSearchPage.relationshipGroupPopup.selectRelationalGroup')}
        </Typography>
      </DialogTitle>
    );

    const renderPopupContent = () => (
      <Box className="aur-data-grid-container">
        <DialogContent>
          <AurDataGridPro
            getRowId={(row) => row.id}
            className={'aur-data-grid'}
            rows={relationalGroupArray}
            columns={columnArray}
            hideFooter
            pageSizeOptions={[relationalGroupArray.length]}
            onCellClick={handleCellClick}
            checkBoxVisible={false}
            disableColumnSorting
            paginationModel={{
              page: 0,
              pageSize: relationalGroupArray.length,
            }}
            addedInDialog
            classNameOfParentScollElement='aur-data-grid-container'
          />
        </DialogContent>
      </Box>
    );

    const renderDialogActions = () => {
      return (
        <DialogActions>
          <Grid
            container
            className={'relationship-group-action-buttons-container'}
          >
            <Grid item>
              <AurButton
                dataTestId={'cancelRelationshipDialogButton'}
                classNames={'typo-text buttonLarge_size cancel-button'}
                type={'exit'}
                size={'Medium'}
                label={t('indexSearchPage.relationshipGroupPopup.cancel')}
                onClick={onCancelPopup}
              />
            </Grid>
            <Grid item>
              <AurButton
                dataTestId={'saveRelationshipDialogButton'}
                classNames={'typo-text buttonLarge_size save-button'}
                type={'continue'}
                size={'Medium'}
                label={t('indexSearchPage.relationshipGroupPopup.save')}
                onClick={onSaveData}
                disabled={isEqual(
                  sortBy(selectedIdArray),
                  sortBy(currentSelectedIdArray)
                )}
              />
            </Grid>
          </Grid>
        </DialogActions>
      );
    };

    return (
      <Dialog
        data-testid={'relationship-group-select-dialog'}
        className={'popup aur-unsaved-changes-popup relationship-group-popup'}
        open={isRelationshipPopupOpen}
        {...rest}
      >
        {renderPopupTitle()}
        {renderPopupContent()}
        {renderDialogActions()}
      </Dialog>
    );
  }
);

export default RelationshipGroupPopup;
